// eslint-disable-next-line @altermaind/you-shall-not-pass
import { ReactElement } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Asset, Box, Card, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { VerticalStepper } from '@/_shared/components/VerticalStepper';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';

type Step = {
  title: string;
  description: ReactElement | string;
};

export const TrainingSoftSkillsAdmin = () => {
  const t = useTranslations('aiPlayground.admin');
  const { genericResponsiveValue } = useResponsiveStyle();

  const gapS = useThemeValue('space', 's');
  const gapXs = useThemeValue('space', 'xs');

  const steps: Step[] = [
    {
      title: t('howDoesItWork.step1.title'),
      description: t('howDoesItWork.step1.description'),
    },
    {
      title: t('howDoesItWork.step2.title'),
      description: t('howDoesItWork.step2.description'),
    },
    {
      title: t('howDoesItWork.step3.title'),
      description: t('howDoesItWork.step3.description'),
    },
  ];

  return (
    <>
      <Box>
        <Box
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
          justifyContent='space-between'
        >
          <Box
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
            alignItems={genericResponsiveValue({
              breakPoints: { desktop: 'center' },
              defaultValue: 'flex-start',
            })}
          >
            <Typography
              mb={genericResponsiveValue({
                breakPoints: { desktop: 'none' },
                defaultValue: 'xs',
              })}
              variant='h1'
            >
              {t('intro.title')}
            </Typography>
            <Box
              ml={genericResponsiveValue({
                breakPoints: { desktop: 'xs' },
                defaultValue: 'none',
              })}
              borderRadius='l'
              mb={genericResponsiveValue({
                breakPoints: { desktop: 'none' },
                defaultValue: 'xs',
              })}
              backgroundColor='primary'
            >
              <Typography py='xxxs' px='xxs' variant='p2/light'>
                {t('intro.labelComingSoon')}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          mt='s'
          maxWidth={genericResponsiveValue({
            breakPoints: { desktop: '50%' },
            defaultValue: '100%',
          })}
        >
          <Typography variant='p2/light'>{t('intro.description')}</Typography>
        </Box>
      </Box>
      <Box
        display='grid'
        gridTemplateColumns={genericResponsiveValue({
          breakPoints: { desktop: 'repeat(3, 1fr)' },
          defaultValue: 'repeat(1fr)',
        })}
        style={{ gap: gapXs }}
        mt='l'
      >
        <Card display='flex' flexDirection='row' backgroundColor='primary'>
          <Box mr='xxs'>
            <Svg name='rocket' size='s' />
          </Box>
          <Box>
            <Typography variant='h4'>{t('pillars.block1.title')}</Typography>
            <Typography mt='xxs' variant='p2/light'>
              {t('pillars.block1.description')}
            </Typography>
          </Box>
        </Card>
        <Card display='flex' flexDirection='row' backgroundColor='primary'>
          <Box mr='xxs'>
            <Svg name='document' size='s' />
          </Box>
          <Box>
            <Typography variant='h4'>{t('pillars.block2.title')}</Typography>
            <Typography mt='xxs' variant='p2/light'>
              {t('pillars.block2.description')}
            </Typography>
          </Box>
        </Card>
        <Card display='flex' flexDirection='row' backgroundColor='primary'>
          <Box mr='xxs'>
            <Svg name='chart' size='s' />
          </Box>
          <Box>
            <Typography variant='h4'>{t('pillars.block3.title')}</Typography>
            <Typography mt='xxs' variant='p2/light'>
              {t('pillars.block3.description')}
            </Typography>
          </Box>
        </Card>
      </Box>
      <Box mt='l'>
        <Typography variant='h2'>{t('howDoesItWork.title')}</Typography>
        <Box
          alignItems='center'
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
          mt='s'
          style={{ gap: gapS }}
        >
          <Box flex='1'>
            <Asset width='100%' name='howItWorks' domain='images' />
          </Box>
          <Box flex='1'>
            <VerticalStepper primaryStyle noBorder steps={steps} />
          </Box>
        </Box>
      </Box>
      <Box mt='l'>
        <Typography variant='h2'>{t('catalog.title')}</Typography>
        <Box
          display='grid'
          gridTemplateColumns={genericResponsiveValue({
            breakPoints: { desktop: 'repeat(4, 1fr)' },
            defaultValue: 'repeat(1, 1fr)',
          })}
          style={{ gap: gapXs }}
          mt='xs'
        >
          <Card p='xxs'>
            <Box>
              <Asset domain='images' width='100%' name='catalog1' />
            </Box>
            <Box>
              <Box display='flex' flexDirection='row' mt='xs'>
                <Svg name='ai' size='s' />
                <Typography ml='xxs' variant='h4'>
                  {t('catalog.step1.title')}
                </Typography>
              </Box>
              <Typography mb='xxs' mt='xxs' variant='p3/light'>
                {t('catalog.step1.description')}
              </Typography>
            </Box>
          </Card>
          <Card p='xxs'>
            <Box>
              <Asset domain='images' width='100%' name='catalog2' />
            </Box>
            <Box>
              <Box display='flex' flexDirection='row' mt='xs'>
                <Svg name='ai' size='s' />
                <Typography ml='xxs' variant='h4'>
                  {t('catalog.step2.title')}
                </Typography>
              </Box>
              <Typography mb='xxs' mt='xxs' variant='p3/light'>
                {t('catalog.step2.description')}
              </Typography>
            </Box>
          </Card>
          <Card p='xxs'>
            <Box>
              <Asset domain='images' width='100%' name='catalog3' />
            </Box>
            <Box>
              <Box display='flex' flexDirection='row' mt='xs'>
                <Svg name='ai' size='s' />
                <Typography ml='xxs' variant='h4'>
                  {t('catalog.step3.title')}
                </Typography>
              </Box>
              <Typography mb='xxs' mt='xxs' variant='p3/light'>
                {t('catalog.step3.description')}
              </Typography>
            </Box>
          </Card>
          <Card p='xxs'>
            <Box>
              <Asset domain='images' width='100%' name='catalog4' />
            </Box>
            <Box>
              <Box display='flex' flexDirection='row' mt='xs'>
                <Svg name='ai' size='s' />
                <Typography ml='xxs' variant='h4'>
                  {t('catalog.step4.title')}
                </Typography>
              </Box>
              <Typography mb='xxs' mt='xxs' variant='p3/light'>
                {t('catalog.step4.description')}
              </Typography>
            </Box>
          </Card>
        </Box>
      </Box>
      <Box mt='l'>
        <Box
          alignItems='center'
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
          mt='s'
          style={{ gap: gapS }}
        >
          <Box alignItems='center' flex='1'>
            <Typography mb='xs' variant='h2'>
              {t('gamification.title')}
            </Typography>
            <Typography mb='s' variant='p2/light'>
              {t('gamification.description')}
            </Typography>
            <Box display='flex' flexDirection='row' mt='s'>
              <Box mr='xxs'>
                <Svg name='ai' size='s' />
              </Box>
              <Box>
                <Typography variant='h4'>{t('gamification.block1.title')}</Typography>
                <Typography mt='xxs' variant='p3/light'>
                  {t('gamification.block1.description')}
                </Typography>
              </Box>
            </Box>
            <Box display='flex' flexDirection='row' mt='s'>
              <Box mr='xxs'>
                <Svg name='ai' size='s' />
              </Box>
              <Box>
                <Typography variant='h4'>{t('gamification.block2.title')}</Typography>
                <Typography mt='xxs' variant='p3/light'>
                  {t('gamification.block2.description')}
                </Typography>
              </Box>
            </Box>
            <Box display='flex' flexDirection='row' mt='s'>
              <Box mr='xxs'>
                <Svg name='ai' size='s' />
              </Box>
              <Box>
                <Typography variant='h4'>{t('gamification.block3.title')}</Typography>
                <Typography mt='xxs' variant='p3/light'>
                  {t('gamification.block3.description')}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box alignItems='center' textAlign='center' flex='1'>
            <Asset maxWidth='600px' width='100%' name='gamification' domain='images' />
          </Box>
        </Box>
      </Box>
    </>
  );
};
